import { useContext } from 'react';
import { SessionContext } from './session-context';
import type { SessionContextValue } from './types';

/**
 * Hook to access session/auth state and actions
 *
 * @returns Session context value with user, loading, error, signIn, signOut
 * @throws Error if used outside AuthProvider
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { user, signIn, signOut } = useSession();
 *
 *   if (!user) {
 *     return <button onClick={signIn}>Login</button>;
 *   }
 *
 *   return (
 *     <div>
 *       <p>Hallo, {user.displayName}!</p>
 *       <button onClick={signOut}>Logout</button>
 *     </div>
 *   );
 * }
 * ```
 */
export function useSession(): SessionContextValue {
  const context = useContext(SessionContext);

  if (!context) {
    throw new Error('useSession must be used within an AuthProvider');
  }

  return context;
}
